import { useSearchParams } from 'next/navigation'
import { useMemo } from 'react'

/**
 * Checks if there is a valid auth session in localStorage. Returns true if
 * Auth0 session keys are present.
 */
export default function useHasAuthSession(): boolean {
  const searchParams = useSearchParams()

  // Check to see if either of our authentication providers have stored auth
  // session information in local storage by searching for the relevant keys
  // each authentication provider uses when a user is authenticated.
  return useMemo(() => {
    if (typeof window === 'undefined') return false
    // Auth0 check
    // Auth0 inserts a record in local storage with a key that follows the below pattern:
    // @@auth0spajs@@::UUID:@@user@@
    // We can check this to see if there is an existing auth session
    const auth0Delimiter = '::'
    const auth0ExpectedKeys = ['@@user@@']
    const auth0Items: Record<string, string> = {}
    Object.entries(window.localStorage).forEach(([key, value]) => {
      const parts = key.split(auth0Delimiter)
      const newKey = parts.at(-1)
      if (newKey) {
        auth0Items[newKey] = value
      }
    })

    return auth0ExpectedKeys.every((k) => k in auth0Items)

    // Rerun this useEffect when search parameters change because when auth0 returns a user to the
    // app from login they set a 'code' and 'state' url parameter that auth0 uses to establish the
    // user session. So re-evaluate when the code param is removed from the url.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])
}
